import {
  LiquidityPool,
  Vault,
  WeiPerEther,
  feeTierToPercentage,
} from 'nftx.js';
import { useFormatter } from 'use-intl';
import { formatEther } from 'viem';
import FormattedEther from '../../elements/FormattedEther';
import Button from '../../elements/Button';
import { V2Position, V2Vault } from '@/core/migrate';
import VaultIcon from '../../elements/VaultIcon';
import { Eth } from '../../elements/Icon/icons/Eth';
import { ExclamationTriangle } from '../../elements/Icon/icons/ExclamationTriangle';
import { useRef, useState } from 'react';
import Icon, { Icons } from '../../elements/Icon';
import Price from '../../elements/Price';
import { ArrowsRightLeft } from '../../elements/Icon/icons/ArrowsRightLeft';
import Settings from '@/presentation/components/widgets/Settings';

const MigrateLiquidity = ({
  isConcentratedRange,
  liquidityPoolId,
  liquidityPools,
  onIsConcentratedRangeChange,
  onLiquidityPoolIdChange,
  position,
  v2Vault,
  vaults,
  withinModal,
  onSubmit,
}: {
  position: V2Position;
  v2Vault: V2Vault;
  vaults: Vault[];
  liquidityPools: LiquidityPool[];
  liquidityPoolId: string;
  onLiquidityPoolIdChange: (liquidityPoolId: string) => void;
  isConcentratedRange: boolean;
  onIsConcentratedRangeChange: (isConcentratedRange: boolean) => void;
  withinModal?: boolean;
  onSubmit: () => void;
}) => {
  const [showVaults, setShowVaults] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { number } = useFormatter();
  const fixedVault = liquidityPools.length === 1;
  const pool = liquidityPools.find((pool) => pool.id === liquidityPoolId);
  const selectedVault = vaults.find((vault) => pool?.vaultId === vault.vaultId);
  const selectedOption = position.migrateOptions.find(
    (o) => o.vaultId === pool?.vaultId
  );

  const maxVToken =
    selectedOption?.maxVToken ?? (position.vToken / WeiPerEther) * WeiPerEther;
  const vTokenRemainder = position.vToken - maxVToken;
  const maxEth = selectedOption?.maxEth ?? position.eth;
  const ethRemainder = position.eth - maxEth;

  const symbolPrefix = position.type === 'xSlp' ? 'x' : '';

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="bg-gray-50 border border-gray-300 rounded-xl p-4">
        <div className="flex space-x-4 items-center font-bold">
          <h3 className="text-lg">Existing position</h3>
          <div className="bg-gray-100 p-2 rounded-lg flex items-center flex-grow">
            <div className="flex items-center mr-2">
              <VaultIcon
                vaultId={position.vaultId}
                legacy={true}
                className="w-4 h-4 mr-2"
              />
              <Eth className="w-4 h-4 mr-4" />
              <span className="font-bold">{`${symbolPrefix}${position.symbol}WETH`}</span>
            </div>
            <div className="border border-gray-300 rounded-md p-1 text-sm mr-4">
              {'0.3%'}
            </div>
            <div className="ml-auto">
              <span className="mr-2">
                {`${number(Number(formatEther(position.vToken)), {
                  maximumFractionDigits: 2,
                })} ${position.symbol}`}
                <span className="text-gray-400">{' / '}</span>
                {`${number(Number(formatEther(position.eth)), {
                  maximumFractionDigits: 2,
                })} ETH`}
              </span>
              <span className="text-gray-400 text-xs uppercase">Balance</span>
            </div>
          </div>
        </div>
        <div className="text-center text-sm text-gray-500 mt-4">
          <ExclamationTriangle className="w-5 h-5 inline-block mr-2" />
          {`Only whole NFT units can be migrated. `}
          <b className="font-bold">{`${number(
            Number(formatEther(vTokenRemainder)),
            { maximumFractionDigits: 2 }
          )} ${v2Vault.token.symbol} and ${number(
            Number(formatEther(ethRemainder)),
            { maximumFractionDigits: 2 }
          )} ETH will stay in your NFTX V2 position.`}</b>
        </div>
      </div>
      <div className="bg-gray-50 border border-gray-300 rounded-xl p-4">
        <hgroup className="flex items-center space-x-4 mb-4">
          <h3 className="text-lg font-bold flex-grow">New Position</h3>
          <span className="ml-auto text-gray-400">
            {'Select pool to migrate liquidity to'}
          </span>
          <div className="relative">
            <Settings />
          </div>
        </hgroup>

        <div className="relative mb-4" ref={dropdownRef}>
          <button
            type="button"
            className="bg-gray-100 border border-gray-300 rounded-md py-6 px-6 flex items-center w-full"
            onClick={() => setShowVaults((x) => !x)}
            disabled={fixedVault}
          >
            <VaultIcon vaultId={selectedVault?.vaultId} className="w-6 h-6" />
            <Eth className="w-6 h-6" />
            <span className="mx-2 font-bold">
              {selectedVault?.token.symbol}
              {'/'}
              {'ETH'}
            </span>
            <span className="border border-gray-300 bg-gray-200 font-bold rounded-lg p-1 text-sm mr-4">
              {'0.3%'}
            </span>
            <span className="uppercase inline-flex items-baseline space-x-1 mr-4 ml-auto">
              <b className="font-bold">{selectedVault?.totalHoldings}</b>{' '}
              <span className="text-xs font-medium text-gray-500">{`holdings`}</span>
            </span>
            <span className="uppercase inline-flex items-baseline space-x-1 mr-4">
              <b className="font-bold">
                <FormattedEther value={pool?.apr['1m']} style="percent" />
              </b>
              <span className="text-xs font-medium text-gray-500">{`APR`}</span>
            </span>
            <span className="truncate mr-4">
              <b className="font-bold truncate">
                <Price value={pool?.totalValueLocked} />
              </b>{' '}
              <span className="text-xs font-medium text-gray-500">{`TVL`}</span>
            </span>
            <span className="ml-4">
              {!fixedVault ? (
                <Icon
                  name={Icons.CHEVRON_DOWN}
                  className="text-gray-400"
                  size={24}
                />
              ) : (
                <Icon
                  name={Icons.CHECK_CIRCLE}
                  className="text-green-500"
                  size={32}
                />
              )}
            </span>
          </button>
          {showVaults && (
            <div
              className={`overflow-hidden bg-gray-100 border border-gray-300 rounded-md w-full absolute top-full left-0 right-0 ${
                withinModal ? 'h-64' : 'h-auto'
              } mt-4 shadow-lg flex flex-col z-10`}
            >
              <div className="flex text-xs text-gray-500 font-medium uppercase mt-2 px-3">
                <div className="w-1/4 p-1">{'Vault'}</div>
                <div className="w-1/4 p-1 text-right">{'Holdings'}</div>
                <div className="w-1/4 p-1 text-right">{'APR'}</div>
                <div className="w-1/4 p-1 text-right">{'TVL'}</div>
              </div>
              <div className="flex-1 overflow-scroll hide-scroll text-sm space-y-1 py-2">
                {liquidityPools.map((pool) => {
                  const vault = vaults.find(
                    (vault) => vault.vaultId === pool.vaultId
                  );
                  if (!vault) {
                    return null;
                  }

                  return (
                    <button
                      key={pool.id}
                      value={pool.id}
                      onClick={() => {
                        onLiquidityPoolIdChange(pool.id);
                        setShowVaults(false);
                      }}
                      className="flex items-center w-full px-3 hover:bg-gray-50 font-medium"
                    >
                      <span className="w-1/4 p-1 text-left">
                        {`${vault.token.symbol} / ETH`}{' '}
                        <span className="border border-gray-300 bg-gray-200 font-bold rounded-lg p-1 text-xs ml-2">
                          {number(feeTierToPercentage(pool.feeTier), {
                            style: 'percent',
                            minimumFractionDigits: 1,
                          })}
                        </span>
                      </span>

                      <span className="w-1/4 p-1 text-right">{`${vault.totalHoldings}`}</span>
                      <span className="w-1/4 p-1 text-right truncate">
                        <FormattedEther
                          value={pool.apr['1m']}
                          style="percent"
                        />
                      </span>
                      <span className="w-1/4 p-1 text-right truncate">
                        <Price value={pool.totalValueLocked} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <hgroup className="flex mb-4">
          <h3 className="text-lg font-bold">Pick liquidity type</h3>
          <span className="ml-auto text-gray-400">
            {`This is the range you'll deposit into`}
          </span>
        </hgroup>

        <div className="flex items-center space-x-4">
          <button
            type="button"
            onClick={() => onIsConcentratedRangeChange(false)}
            className={`${
              !isConcentratedRange ? 'opacity-100' : 'opacity-50'
            } transition-opacity`}
          >
            {'Infinite range'}
          </button>
          <button
            type="button"
            onClick={() => onIsConcentratedRangeChange(!isConcentratedRange)}
            className="flex w-10 p-1 rounded-full h-6 bg-pink-500 relative"
          >
            <span
              className={`rounded-full bg-gray-50 absolute w-4 h-4 top-1 transition-[left] ${
                isConcentratedRange ? 'left-5' : 'left-1'
              }`}
            />
          </button>
          <button
            type="button"
            onClick={() => onIsConcentratedRangeChange(true)}
            className={`${
              isConcentratedRange ? 'opacity-100' : 'opacity-50'
            } transition-opacity`}
          >
            {'Concentrated range'}
          </button>
        </div>
        <div className="mt-4 bg-gray-100 rounded-md p-4">
          <dl className="flex space-x-6 items-center">
            <div className="space-y-2">
              <dt className="text-gray-500 text-sm">{'Lower Price'}</dt>
              <dd className="font-bold">
                {isConcentratedRange
                  ? `-90% (${number(
                      Number(formatEther(selectedVault?.vTokenToEth || 0n)) *
                        0.1,
                      { maximumFractionDigits: 6 }
                    )} ETH)`
                  : '0.0001 ETH'}
              </dd>
            </div>
            <div>
              <ArrowsRightLeft className="w-6 h-6 text-gray-400" />
            </div>
            <div className="space-y-2">
              <dt className="text-gray-500 text-sm">{'Upper Price'}</dt>
              <dd className="font-bold">
                {isConcentratedRange
                  ? `+1,000% (${number(
                      Number(formatEther(selectedVault?.vTokenToEth || 0n)) * 10
                    )} ETH)`
                  : '1,000 ETH'}
              </dd>
            </div>
          </dl>
        </div>
        <div className="border border-blue-500/30 bg-blue-500/10 p-4 rounded-md mt-4 text-blue-500">
          {
            // @todo get amount of eth returned
          }
          {isConcentratedRange
            ? 'Concentrated ranges are narrower and more capital efficient, while reducing the price impact and increasing the chance of vault activity.'
            : 'An "infinite range" is similar to the Sushi-based position you have on V2, but capped at both ends.'}
        </div>
      </div>
      <div>
        <Button
          type="submit"
          disabled={!liquidityPoolId}
          className="w-full"
          size="large"
        >
          Migrate
        </Button>
      </div>
    </form>
  );
};

export default MigrateLiquidity;
